import React, { useState, useEffect, useRef } from "react";
import { useToaster } from "@hellocontento/maillard";
import { format } from "date-fns";
import { callApi } from "utils/ContentoApi";
import ConfirmationDialog from "components/modals/confirmationModal/ConfirmationDialog";
import ContentTypeBadge from "components/common/contentTypes/ContentTypeBadge";
import ReactMarkdown from "react-markdown"

import * as styles from "../styles"
import { parseProfileSections } from "../utils"
import { CATEGORY_TO_CONTENT_TYPE } from "../values"

interface TextRowProps {
  accountId: string;
  profile: {
    id: string;
    name: string;
    createdAt: string; // Formatted date string
    content: string;
  };
  knownUpdatedAt: string; // the card's last-seen profile updatedAt, sent as the optimistic lock
  onUpdatedAtChange: (updatedAt: string) => void;
  // Reports the save status up so the profile-card header can show it.
  onSaveStatusChange?: (status: { text: string; isError: boolean }) => void;
}

export const TextRow: React.FC<TextRowProps> = ({
  accountId,
  profile,
  knownUpdatedAt,
  onUpdatedAtChange,
  onSaveStatusChange,
}) => {
  const addToast = useToaster();
  const [activeEditorTab, setActiveEditorTab] = useState<"edit" | "view">("view");
  // Seed from props once on mount; after that TextRow owns the content for its
  // entire lifetime. The parent never pushes updates back down.
  const [editedContent, setEditedContent] = useState(profile.content);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState(false);
  const [hasConflict, setHasConflict] = useState(false);
  const shouldSkipNextAutoSave = useRef(true);

  const [showConflictDialog, setShowConflictDialog] = useState(false);
  // Server's canonical state from a 409 — held until the user resolves the
  // dialog. "Load their changes" pushes it into local state; "Keep my changes"
  // discards it and force-saves.
  const [conflictServerState, setConflictServerState] = useState<{ content: string; updatedAt: string } | null>(null);

  const getSaveStatusText = () => {
    if (hasConflict) return "Conflict detected";
    if (saveError) return "Failed to save";
    if (lastSaved) return `Saved at ${format(lastSaved, "HH:mm")}`;
    return "Saved";
  };

  const handleOverwrite = async () => {
    setShowConflictDialog(false);
    setHasConflict(false);
    setConflictServerState(null);
    // Save without expectedUpdatedAt to force overwrite
    try {
      const response = await callApi({
        method: "put",
        url: `content-factory/accounts/${accountId}/profiles/${profile.id}/content`,
        data: { content: editedContent }
      }, true, true);
      setLastSaved(new Date());
      onUpdatedAtChange(response.updatedAt);
      setSaveError(false);
    } catch (error) {
      console.error("Failed to overwrite profile", error);
      setSaveError(true);
      addToast("Failed to save profile", "error");
    }
  };

  const handleLoadTheirChanges = () => {
    setShowConflictDialog(false);
    setHasConflict(false);
    if (conflictServerState) {
      // Skip the next auto-save: we're replacing editedContent with the
      // server's content, and we don't want that programmatic change to
      // immediately turn around and PUT it back.
      shouldSkipNextAutoSave.current = true;
      setEditedContent(conflictServerState.content);
      onUpdatedAtChange(conflictServerState.updatedAt);
      setConflictServerState(null);
    }
  };

  // Auto-save logic
  useEffect(() => {
      if (shouldSkipNextAutoSave.current) {
          shouldSkipNextAutoSave.current = false;
          return;
      }

      // Don't auto-save if there's a conflict
      if (hasConflict) return;

      const timer = setTimeout(async () => {
          try {
              const response = await callApi({
                  method: "put",
                  url: `content-factory/accounts/${accountId}/profiles/${profile.id}/content`,
                  data: {
                      content: editedContent,
                      expectedUpdatedAt: knownUpdatedAt,
                  }
              }, true, true);
              setLastSaved(new Date());
              onUpdatedAtChange(response.updatedAt);
              setSaveError(false);
          } catch (error: any) {
              if (error.status === 409) {
                  console.error("Profile conflict detected", error);
                  setConflictServerState({ content: error.data.content, updatedAt: error.data.updatedAt });
                  setHasConflict(true);
                  setShowConflictDialog(true);
              } else {
                  console.error("Failed to save profile", error);
                  setSaveError(true);
                  addToast("Failed to save profile", "error");
              }
          }
      }, 1000);

      return () => clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedContent]);

  // Surface the save status to the parent (shown in the profile-card header).
  useEffect(() => {
    onSaveStatusChange?.({ text: getSaveStatusText(), isError: saveError || hasConflict });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSaved, saveError, hasConflict, onSaveStatusChange]);

  // Render the saved profile as a two-column field grid. An odd number of
  // sections makes the last one span both columns so there's no lonely half-row.
  const sections = parseProfileSections(editedContent);
  const lastSectionIsWide = sections.length % 2 === 1;

  return (
    <>
      <styles.ProfileSection>
        <styles.SegMini>
          <styles.SegMiniButton isActive={activeEditorTab === "view"} onClick={() => setActiveEditorTab("view")}>
            View
          </styles.SegMiniButton>
          <styles.SegMiniButton data-testid="profile-edit-tab" isActive={activeEditorTab === "edit"} onClick={() => setActiveEditorTab("edit")}>
            Edit
          </styles.SegMiniButton>
        </styles.SegMini>

        {activeEditorTab === "edit" ? (
          <styles.ProfileTextArea
              value={editedContent}
              onChange={(event) => setEditedContent(event.target.value)}
          />
        ) : sections.length > 0 ? (
          <styles.FieldsGrid>
            {sections.map((section, index) => (
              <styles.Field key={`${section.key}-${index}`} $wide={lastSectionIsWide && index === sections.length - 1}>
                <styles.FieldKey>{section.key}</styles.FieldKey>
                <styles.FieldValue>
                  <ReactMarkdown>{section.body}</ReactMarkdown>
                </styles.FieldValue>
              </styles.Field>
            ))}
            <styles.Field $wide>
              <styles.FieldKey>Content categories</styles.FieldKey>
              <styles.FieldValue>
                <styles.ThemeChips>
                  {Object.values(CATEGORY_TO_CONTENT_TYPE).map((contentTypeId) => (
                    <ContentTypeBadge
                      key={contentTypeId}
                      contentTypeId={contentTypeId}
                      postIdea={null}
                      maxWidth={undefined}
                      variant="dark"
                      size="sm"
                    />
                  ))}
                </styles.ThemeChips>
              </styles.FieldValue>
            </styles.Field>
          </styles.FieldsGrid>
        ) : (
          <styles.MarkdownPreview>
            <ReactMarkdown>{editedContent}</ReactMarkdown>
          </styles.MarkdownPreview>
        )}
      </styles.ProfileSection>

      <ConfirmationDialog
        isOpen={showConflictDialog}
        title="Profile Modified"
        description="This profile was modified by someone else while you were editing. What would you like to do?"
        cancelLabel="Keep My Changes"
        confirmLabel="Load Their Changes"
        onCancel={handleOverwrite}
        onConfirm={handleLoadTheirChanges}
      />
    </>
  );
};

export default TextRow;
